import { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { TableCell } from '@mui/material';
import {
  Typography,
  TableWrapperFooter,
  TableIconCell,
  TableDots,
  Pagination,
  Button,
  Chip,
  enqueueSnackbar,
  confirmEmployeeStatusModal,
  SimpleTable,
  employeeEditModal,
  TableHeadCell,
} from '../../../design-system';
import { DashboardHeader } from '../../dashboard';
import { Filters } from './Filters';
import { useStore } from '../../../context';
import {
  useStatesWithFilterCounter,
  usePaginationState,
  useEmployeesQuery,
  useActivateEmployeeMutation,
  useDeactivateEmployeeMutation,
} from '../../../hooks';
import { showToRoles, formatPhoneNumber, getEmployeeOptions } from '../../../utils';
import {
  employeesTableCompanyTitle,
  employeesRolesLabel,
  TABLE,
  MESSAGES,
  EmployeeAccountStatusToChipColors,
  EmployeeAccountStatusToChipLabels,
} from '../../../const';
import { i18n } from '../../../i18n';
import { EmployeeGetAllQuery, Roles } from '../../../types';

interface EmployeesPageProps {
  innerScroll?: boolean;
}

export const EmployeesPage = styled.div<EmployeesPageProps>`
  display: grid;
  grid-template-rows: max-content 1fr;
  grid-row-gap: 3rem;

  height: 100%;

  ${(props) =>
    props.innerScroll &&
    css`
      overflow: hidden;
    `}
`;

export const EmployeesHeader = ({ totalCount }: any) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();

  return (
    <DashboardHeader>
      <Typography variant={'textXL'} weight={'bold'}>
        {i18n.t('pages.employees.title', { totalCount })}
      </Typography>
      {showToRoles(
        [
          'SUPER_ADMIN',
          'YARD_COMPANY_ADMIN',
          'YARD_MANAGER',
          'CLIENT_MANAGER',
          ...((user.yardEmployee?.canCreateClient ? ['YARD_DISPATCHER'] : []) as Roles[]),
        ],
        user,
      ) && (
        <Button size={'medium'} to={'/dashboard/employees/add'}>
          {i18n.t('button.addEmployee')}
        </Button>
      )}
    </DashboardHeader>
  );
};

export const EmployeesBody = ({ setTotalCount }: any) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const { page, take, skip, handlePageChange } = usePaginationState();
  const activateMutation = useActivateEmployeeMutation();
  const deactivateMutation = useDeactivateEmployeeMutation();
  const [openDotsMenuIndex, setOpenMenuDotsSelect] = useState(null);
  const [states, setStates, filteredStates] = useStatesWithFilterCounter(
    {
      belongsTo: 'All',
      companyName: '',
      companyId: '',
      firstName: '',
      lastName: '',
      accountStatus: 'All',
    },
    {
      belongsTo: showToRoles(
        [
          'SUPER_ADMIN',
          'YARD_COMPANY_ADMIN',
          'YARD_COMPANY_VIEWER',
          'YARD_MANAGER',
          'YARD_DISPATCHER',
        ],
        user,
      )
        ? 'Yard'
        : 'Client',
      accountStatus: 'ACTIVE',
    },
  );

  const {
    data = { data: [], totalCount: 0 },
    isFetching,
    refetch,
  } = useEmployeesQuery({
    ...filteredStates,
    take,
    skip,
  });

  useEffect(() => {
    if (isFetching) return;

    setTotalCount(data.totalCount);
  }, [isFetching]);

  const handleDotsToggle = (i: any) => {
    if (openDotsMenuIndex !== i) return setOpenMenuDotsSelect(i);

    setOpenMenuDotsSelect(null);
  };

  const handleFilterChange: typeof setStates = (...args) => {
    handlePageChange(null, 1);
    setStates(...args);
  };

  const handleDotsOptionSelect = (
    employee: EmployeeGetAllQuery['data'][number],
    val: any,
    id: any,
  ) => {
    let fc = null;

    if (val === 'EDIT') {
      employeeEditModal.employee = employee;
      employeeEditModal.id = id;
      employeeEditModal.submitClick = () => {
        refetch();
      };
      employeeEditModal.open();
    }
    if (val === 'ACTIVATE') {
      fc = () =>
        activateMutation.mutate(
          { userId: id },
          {
            onSuccess: async () => {
              enqueueSnackbar(MESSAGES.EMPLOYEE.SUCCESS.ACTIVATED, 'success');
              await refetch();
            },
            onError: () => {
              enqueueSnackbar(MESSAGES.ERROR_BOUNDARY.DEFAULT, 'error');
            },
          },
        );
    }
    if (val === 'DEACTIVATE') {
      fc = () =>
        deactivateMutation.mutate(
          { userId: id },
          {
            onSuccess: async () => {
              enqueueSnackbar(MESSAGES.EMPLOYEE.SUCCESS.DEACTIVATED, 'success');
              await refetch();
            },
            onError: () => {
              enqueueSnackbar(MESSAGES.ERROR_BOUNDARY.DEFAULT, 'error');
            },
          },
        );
    }
    if (val === 'ACTIVATE' || val === 'DEACTIVATE') {
      confirmEmployeeStatusModal.val = val;
      confirmEmployeeStatusModal.fc = fc;
      confirmEmployeeStatusModal.open();
    }
  };

  return (
    <SimpleTable
      data={data}
      filters={() => (
        <Filters
          setStates={handleFilterChange}
          {...states}
          belongsTo={states.belongsTo}
          handlePageChange={handlePageChange}
        />
      )}
      tableRowProps={(employee, i) => ({
        key: employee.user.id + i,
        onClick: () => {},
      })}
      columns={[
        {
          column: 1,
          headText: () => i18n.t('label.actions'),
          body: (employee, i) => (
            <TableIconCell component={'th'} align={'left'}>
              {employee.user.id !== user.id &&
                !!getEmployeeOptions(employee, user, states.accountStatus).length && (
                  <TableDots
                    open={openDotsMenuIndex === i}
                    onToggle={() => handleDotsToggle(i)}
                    options={getEmployeeOptions(employee, user, states.accountStatus)}
                    onSelect={(val: any) => {
                      handleDotsOptionSelect(employee, val, employee.user.id);
                    }}
                  />
                )}
            </TableIconCell>
          ),
        },
        {
          headText: () => employeesTableCompanyTitle[states.belongsTo],
          bodyText: (employee) => employee.companyName,
        },
        {
          head: () => (
            <TableHeadCell id={'column-role'} align={'left'}>
              {i18n.t('label.role')}
            </TableHeadCell>
          ),
          bodyText: (employee) => employeesRolesLabel[employee.role],
        },
        {
          headText: () => i18n.t('label.firstName'),
          bodyText: (employee) => employee.user.firstName,
        },
        {
          headText: () => i18n.t('label.lastName'),
          bodyText: (employee) => employee.user.lastName,
        },
        {
          headText: () => i18n.t('label.email'),
          bodyText: (employee) => employee.user.email,
        },
        {
          headText: () => i18n.t('label.phoneNumber'),
          bodyText: (employee) => formatPhoneNumber(employee.user.phoneNumber),
        },
        {
          headText: () => i18n.t('label.accountStatus'),
          body: (employee) => (
            <TableCell align={'left'}>
              <Chip
                size={'small'}
                label={EmployeeAccountStatusToChipLabels[employee.user.accountStatus]}
                color={EmployeeAccountStatusToChipColors[employee.user.accountStatus]}
              />
            </TableCell>
          ),
        },
      ]}
      footer={() => (
        <TableWrapperFooter>
          <Pagination
            count={Math.ceil(data.totalCount / TABLE.PAGE_MAX_SIZE)}
            page={page}
            onChange={handlePageChange}
          />
        </TableWrapperFooter>
      )}
    />
  );
};
